import React, { Component, Fragment } from "react";

import School from "../models/School";
import SchoolCell from "./SchoolCell";
import * as _ from "lodash";
import moment from "moment";
import Table, { TableColumn, TableItem } from "./Table";
import TableCell from "./TableCell";
import { TableRowProps } from "./TableRow";
import { Checkbox, DefaultButton } from "office-ui-fabric-react";

interface SchoolListProps {
    title: string;
    enableRowLinks: boolean;
    schools: School[];
    loading?: boolean;
    selectable?: boolean;
    onSelectionChange?: (selection: Map<string, boolean>) => void
}

interface SchoolListState {
    schoolsSelected: Map<string, boolean>
    lastSelectedSchoolKey: string | null;
    sortedSchools: School[];
    selectAll: boolean;
}

export default class SchoolList extends Component<SchoolListProps, SchoolListState>
{
    constructor(props: SchoolListProps) {
        super(props);

        this.state = {
            schoolsSelected: new Map<string, boolean>(),
            lastSelectedSchoolKey: null,
            sortedSchools: props.schools,
            selectAll: false
        }
    }
    public componentDidMount() {

    }

    private static columns: TableColumn[] = [
        {
            title: "Name",
            propertyPath: "schoolName",
            flex: 4,
            enableSort: true,
        },
        {
            title: "Tenant",
            propertyPath: "tenantName",
            flex: 4,
            enableSort: true
        },
        {
            title: "Created Date",
            propertyPath: "createdDate",
            flex: 2,
            type: "date",
            enableSort: true
        },
        {
            title: "Onboard Status",
            propertyPath: "onboardStatus",
            flex: 2,
            enableSort: true
        },
        {
            title: "License Ref",
            propertyPath: "licenseReference",
            flex: 3,
            enableSort: true
        },
        {
            title: "License",
            propertyPath: "license",
            flex: 2,
            enableSort: true
        },
        {
            title: "Expires",
            propertyPath: "licenseEndDate",
            flex: 2,
            enableSort: true,
            sortFunc: SchoolList.licenseEndDateSort
        },
        {
            title: "Third Party SDS",
            propertyPath: "thirdPartySDS",
            flex: 2,
            enableSort: true,
        },
        {
            title: "",
            propertyPath: "disabled",
            flex: 2,
            enableSort: true
        },
        {
            title: "",
            propertyPath: "selection",
            flex: 2,
            enableSort: false
        }

    ];

    private rowProps = (school: School): TableRowProps => {
        return {
            to: `/cdb-admin/schools/${school.tenantId}/${school.schoolCode}`,
            enableRowLink: this.props.enableRowLinks
        };
    }

    public static getSelectedSchoolKey(school: School): string {
        return `${school.schoolCode}_${school.tenantId}`
    }

    private onChecked = (school: School, isChecked: boolean | undefined, isShiftClick: boolean) => {
        if (school?.schoolCode != undefined && isChecked != undefined) {
            let lastSelectedSchool: School | null = null;
            if (this.state.lastSelectedSchoolKey !== null) {
                lastSelectedSchool = this.props.schools.find(t => SchoolList.getSelectedSchoolKey(t) === this.state.lastSelectedSchoolKey) || null;
            }

            if (isShiftClick && lastSelectedSchool !== null && isChecked) {
                const indexes = [this.state.sortedSchools.indexOf(school), this.state.sortedSchools.indexOf(lastSelectedSchool)].sort((a, b) => a - b);
                const start = indexes[0];
                const end = indexes[1];

                this.state.sortedSchools.slice(start, end + 1).forEach(s => {
                    this.state.schoolsSelected.set(SchoolList.getSelectedSchoolKey(s), true);
                });
            }
            else {
                this.state.schoolsSelected.set(SchoolList.getSelectedSchoolKey(school), isChecked);
            }

            this.setState({ schoolsSelected: this.state.schoolsSelected, lastSelectedSchoolKey: isChecked ? SchoolList.getSelectedSchoolKey(school) : null });
            this.props.onSelectionChange && this.props.onSelectionChange(this.state.schoolsSelected);
        }
    }

    private onSelectAll(isChecked: boolean | undefined) {
        if (isChecked !== undefined) {
            this.props.schools.forEach(school => {
                if (school.schoolCode !== undefined) {
                    this.state.schoolsSelected.set(SchoolList.getSelectedSchoolKey(school), isChecked);
                }
            });
            this.setState({ schoolsSelected: this.state.schoolsSelected, selectAll: isChecked });
            this.props.onSelectionChange && this.props.onSelectionChange(this.state.schoolsSelected);
        }
    }

    private SelectOneSchoolFromEachTenant = () => {

        this.state.schoolsSelected.clear();

        const tenants = _.groupBy(this.props.schools, 'tenantId');
        Object.entries(tenants).map((entry) => this.state.schoolsSelected.set(SchoolList.getSelectedSchoolKey(entry[1][0]), true));

        this.setState({ schoolsSelected: this.state.schoolsSelected, selectAll: false });
        this.props.onSelectionChange && this.props.onSelectionChange(this.state.schoolsSelected);
    }

    private isChecked(school: School): boolean {
        if (school?.schoolCode != undefined) {
            var value = this.state.schoolsSelected.get(SchoolList.getSelectedSchoolKey(school));
            return value ?? false;
        }
        return false;
    }

    private buildRow = (school: School, column: TableColumn, index: number): JSX.Element | undefined => {
        switch (column.propertyPath) {
            case "schoolName":
                return <SchoolCell key={index} schoolKey={school} school={school} enableLink={!this.props.enableRowLinks} flex={column.flex} />

            case "tenantName":
                return <TableCell key={index} flex={column.flex}>
                    <span>{school.tenantName}</span>
                    <span className={"cdb-table__extra-detail"}>{school.tenantId}</span>
                </TableCell>;

            case "license":
                return <TableCell key={index} flex={column.flex}>
                    {school.license === "unknown"
                        ? <div className={`pill pill--amber pill--small`}>{_.capitalize(school.license)}</div>
                        : _.capitalize(school.license)}
                </TableCell>;

            case "licenseEndDate":
                return <TableCell key={index} flex={column.flex}>
                    {this.getLicenseEndDateInfo(school)}
                </TableCell>;

            case "disabled":
                return <TableCell key={index} flex={column.flex}>
                    {school.disabled
                        ? <div className={`pill pill--red pill--small`}>Disabled</div>
                        : <div></div>}
                </TableCell>;

            case "thirdPartySDS":
                return <TableCell key={index} flex={column.flex}>
                    {school.thirdPartySDS ? <div>Yes</div> : <div>No</div>}
                </TableCell>;

            case "selection":
                if (this.props.selectable) {
                    return <TableCell key={index} flex={column.flex}>
                        <div style={{ alignItems: "flex-end" }} className="cdb-table__cell flex-item--flex-1">
                            <Checkbox checked={this.isChecked(school)} onChange={(e, isChecked) => {
                                const nativeEvent = e?.nativeEvent as any;
                                this.onChecked(school, isChecked, nativeEvent.shiftKey)
                            }
                            } />
                        </div>
                    </TableCell>;
                }
                return <div key={index}/>;

        }
        
        return undefined;
    }

    private buildHeaders = (column: TableColumn, index: number): JSX.Element | undefined => {
        switch (column.propertyPath) {
            case "selection":
                if (this.props.selectable) {
                    return <TableCell key={index} flex={column.flex}>
                        <div className="flex-row">
                            <DefaultButton
                                iconProps={{ iconName: "CheckList" }}
                                title={`Select one school for each tenant`}
                                onClick={this.SelectOneSchoolFromEachTenant}
                            />
                            <div style={{ alignItems: "flex-end" }} className="cdb-table__cell flex-item--flex-1">
                                <Checkbox checked={this.state.selectAll} onChange={(e, isChecked) => this.onSelectAll(isChecked)} />
                            </div>
                        </div>
                    </TableCell>;
                }
                return <div key={index} />;
        }
    }

    private static licenseEndDateSort(a: School, b: School): number {
        var dateA = moment(a.licenseEndDate);
        var dateB = moment(b.licenseEndDate);

        if (!dateA.isValid()) {
            return -1;
        }
        if (!dateB.isValid()) {
            return 1;
        }

        return dateA.valueOf() - dateB.valueOf()
    }

    private getLicenseEndDateInfo(school: School) {
        var date = moment(school.licenseEndDate);

        var dateString = date.format("MMM YYYY");
        var dateStringClasses: string = "";

        var monthsRemaining = "";
        var monthsRemaingStyle: any = {};

        if (dateString == "Invalid date") {
            dateString = "Not Set";
            dateStringClasses = `pill pill--red pill--small`;
        }
        else {
            var now = moment();
            var months = date.diff(now, 'months', true);
            months = Math.floor(months) + 1; // Add 1 since licese expires at end of month

            monthsRemaining = `${months} Months(s) remaining`
            if (months < 1) {
                monthsRemaingStyle["color"] = "Orange";
                dateStringClasses = `pill pill--amber pill--small`;
            }
            if (months < 0) {
                monthsRemaingStyle["color"] = "Red";
                dateStringClasses = `pill pill--red pill--small`;
            }
        }

        return (
            <Fragment>
                <div className={dateStringClasses}>{dateString}</div>
                <div className={"cdb-table__extra-detail"} style={monthsRemaingStyle}> {monthsRemaining}</div>
            </Fragment>
        );
    }

    private onSortChange() {
        return (sortedItems: TableItem[]) => {
            var schools = sortedItems as School[];
            this.setState({ sortedSchools: schools });
        };
    }

    public render() {
        return (
            <Table
                title={this.props.title}
                items={this.props.schools}
                emptyMessage={"No schools found matching filter"}
                columns={SchoolList.columns}
                cellForColumn={this.buildRow}
                cellForHeader={this.buildHeaders}
                rowPropsForItem={this.rowProps}
                loading={this.props.loading}
                enableSort={true}
                onSortChange={this.onSortChange()}
                defaultSortColumnPath="schoolName">               
                {this.props.children}
            </Table>    
        );
    }
}